import React from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SurveyActionIcon } from './SurveyActionIcon';
import type { ActionButtonComponentConfig, FlowComponentRenderProps } from '../types';

type Props = FlowComponentRenderProps<ActionButtonComponentConfig> & { pending?: boolean };

/** Canonical commands are executed by the session's persistence/navigation gate. */
export default function FeedbackActionButton(props: Props) {
  const { config, onInteract, pending = false } = props;
  if (config.canonicalActions?.length)
    return (
      <Stack spacing={0.75}>
        {config.canonicalActions.map((action) => (
          <FeedbackActionButton {...props} key={action.canonicalActionId} config={action} />
        ))}
      </Stack>
    );
  const outlined = config.style === 'outlined';
  const text = config.style === 'text';
  return (
    <Button
      type="button"
      fullWidth
      disableElevation
      disabled={pending}
      aria-busy={pending || undefined}
      variant={text ? 'text' : outlined ? 'outlined' : 'contained'}
      onClick={() =>
        onInteract?.({
          componentId: config.id,
          metadata: config.canonicalActionId ? { actionId: config.canonicalActionId } : undefined,
          type: config.action,
          label: config.label,
          url: config.url,
          phone: config.phone,
          value: config.routeValue ?? true,
        })
      }
      sx={{
        minHeight: config.minHeight ?? 48,
        borderRadius: `${config.borderRadius ?? 8}px`,
        px: 2,
        py: 1,
        gap: 1,
        textTransform: 'none',
        color:
          config.textColor ??
          (text
            ? 'var(--cx-brand-text)'
            : outlined
              ? 'var(--cx-brand-button-text)'
              : 'var(--cx-brand-primary-contrast)'),
        bgcolor:
          config.backgroundColor ??
          (text
            ? 'transparent'
            : outlined
              ? 'var(--cx-brand-button-bg)'
              : 'var(--cx-brand-primary)'),
        borderColor: config.borderColor ?? 'var(--cx-brand-button-border)',
        '&:hover': {
          bgcolor:
            config.hoverBackgroundColor ??
            (text || outlined ? 'var(--cx-brand-button-hover-bg)' : 'var(--cx-brand-primary-dark)'),
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, textAlign: config.textAlign ?? 'center' }}>
        <Typography component="span" sx={{ fontSize: config.fontSize ?? 15, fontWeight: 600, lineHeight: '26px' }}>
          {config.label}
        </Typography>
        {config.description && (
          <Typography component="span" sx={{ display: 'block', fontSize: 12 }}>
            {config.description}
          </Typography>
        )}
      </Box>
      <SurveyActionIcon name={config.actionIcon} defaultArrow={config.showArrow} />
    </Button>
  );
}
